import { useCallback, useEffect } from "react";
import { TreeMarking, CreateTreeMarking, UpdateTreeMarking } from "../types/models/TreeMarking.d";
import { UpdateEntityTreeMarking } from "../types/models/TreeMarking";
import { useGetTreeMarkingListQuery, useInsertTreeMarkingMutation, useUpdateTreeMarkingMutation, useDeleteTreeMarkingMutation, } from "../apis/TreeMarking.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useTreeMarking(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertTreeMarkingMutation, { 
        isLoading: insertTreeMarkingLoading, 
        isSuccess: insertTreeMarkingIsSuccess,
        isError: insertTreeMarkingIsError,
        error: insertTreeMarkingError,
    }] = useInsertTreeMarkingMutation()
    const [updateTreeMarkingMutation, { 
        isLoading: updateTreeMarkingLoading,
        isSuccess: updateTreeMarkingIsSuccess,
        isError: updateTreeMarkingIsError,
        error: updateTreeMarkingError,
    }] = useUpdateTreeMarkingMutation()
    const [deleteTreeMarkingMutation, { 
        isLoading: deleteTreeMarkingLoading,
        isSuccess: deleteTreeMarkingIsSuccess,
        isError: deleteTreeMarkingIsError,
        error: deleteTreeMarkingError,
    }] = useDeleteTreeMarkingMutation()

    const loading =
        insertTreeMarkingLoading ||
        updateTreeMarkingLoading ||
        deleteTreeMarkingLoading ||
        false

    const insertTreeMarking = useCallback(async (treeMarking: CreateTreeMarking, redirectPath: string|undefined) => {
        try {
            const data = await insertTreeMarkingMutation(treeMarking).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertTreeMarkingMutation, navigate])
    
    useEffect(() => {
        if (insertTreeMarkingIsSuccess) {
            openSuccessAlert('Tree Marking created')
        } else if (insertTreeMarkingIsError) {
            console.log("add error", insertTreeMarkingError)
            openFailAlert(t`Tree Marking add error: ` + insertTreeMarkingError?.data?.message)
        }
    }, [
        insertTreeMarkingIsSuccess,
        insertTreeMarkingIsError,
        insertTreeMarkingError
    ]);

    const updateTreeMarking = useCallback(async (treeMarking: UpdateEntityTreeMarking, redirectPath: string|undefined) => {
        try {
            const data = await updateTreeMarkingMutation(treeMarking).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateTreeMarkingMutation, navigate])
    
    useEffect(() => {
        if (updateTreeMarkingIsSuccess) {
            openSuccessAlert(t`Tree Marking updated`)
        } else if (updateTreeMarkingIsError) {
            console.log("update error", updateTreeMarkingError)
            openFailAlert(t`Tree Marking update error: ` + updateTreeMarkingError?.data?.message)
        }
    }, [
        updateTreeMarkingIsSuccess,
        updateTreeMarkingIsError,
        updateTreeMarkingError
    ]);

    const deleteTreeMarking = useCallback((treeMarking: TreeMarking, redirectPath: string|undefined) => {
        openDialog(async () => {
            treeMarking = {
                ...treeMarking,
            }
            try {
                const { data } = await deleteTreeMarkingMutation(treeMarking);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteTreeMarkingMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteTreeMarkingIsSuccess) {
            openSuccessAlert(t`Tree Marking deleted`)
        } else if (deleteTreeMarkingIsError) {
            console.log("delete error", deleteTreeMarkingError)
            openFailAlert(t`Tree Marking delete error: ` + deleteTreeMarkingError?.data?.message)
        }
    }, [
        deleteTreeMarkingIsSuccess,
        deleteTreeMarkingIsError,
        deleteTreeMarkingError
    ]);

    return {
        loading,
        insertTreeMarking,
        updateTreeMarking,
        deleteTreeMarking
    }
}
